import React, { useState, useEffect } from "react";
import SEO from "../seo";
const ConfigurePayment = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "online-payments", "cash-on-delivery"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Configure Payment and Checkout Process – xPAGE Help Center"
        description="Learn how to set up payment gateways, enable Cash on Delivery (COD), and customize checkout fields in your xPage store for a smooth and secure customer experience."
        keywords="xPAGE, payment setup, checkout process, online payments, cash on delivery, payment gateways, eCommerce setup"
        canonical="https://help.xpage.ai/help/landing-page-compliance/Configure%20Payment%20and%20Checkout%20Process"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Configure Payment and Checkout Process
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Setting up your payment gateways and customizing checkout ensures your
          customers can complete purchases easily, securely, and with
          confidence. xPage supports multiple online payment methods and Cash on
          Delivery (COD), with fully customizable checkout fields.
        </p>

        {/* Overview Section */}
        <section id="overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              With xPage, you can:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Accept payments via major providers such as{" "}
                <strong>Stripe, PayPal, Airwallex</strong>, and more.
              </li>
              <li>
                Offer <strong>Cash on Delivery (COD)</strong> with a fully
                customizable form.
              </li>
              <li>
                Set your store's <strong>default currency</strong> for
                transactions.
              </li>
              <li>
                Customize checkout fields to match your business needs,
                including customer accounts, phone numbers, and delivery
                instructions.
              </li>
            </ul>
          </div>
        </section>

        {/* Set Up Online Payment Gateways Section */}
        <section id="online-payments" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Set Up Online Payment Gateways
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to the <strong>xPage Admin</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Navigate to <strong>Settings</strong> →{" "}
                  <strong>Payments</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Under <strong>Online Payments</strong>, click{" "}
                  <strong>Choose a Provider</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select your provider (e.g., Stripe, PayPal, Airwallex)
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Follow the prompts to log in to your payment provider account
                  and authorize the connection
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click <strong>Save</strong>
                </span>
              </li>
            </ol>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> For multiple
                currencies, ensure your payment provider supports your desired
                currency list.
              </p>
            </div>
          </div>
        </section>

        {/* Enable Cash on Delivery Section */}
        <section id="cash-on-delivery" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Enable Cash on Delivery (COD)
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              xPage lets you offer COD while keeping your checkout flexible and
              tailored to your store's needs.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  In the <strong>xPage Admin</strong>, go to{" "}
                  <strong>Settings</strong> → <strong>Payments</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Scroll to <strong>Cash on Delivery</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Toggle <strong>Enable COD</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Customize the COD form fields:
                </span>
                <ul className="list-disc list-inside ml-6 mt-1 space-y-1 text-system600">
                  <li>
                    <strong>Full Name</strong> – required for all COD orders.
                  </li>
                  <li>
                    <strong>Phone Number</strong> – required to contact
                    customers for delivery.
                  </li>
                  <li>
                    <strong>Address Fields</strong> – country, city, street
                    address, and ZIP code (optional depending on your shipping
                    region).
                  </li>
                  <li>
                    <strong>Delivery Instructions / Order Notes</strong> –
                    optional for special requests.
                  </li>
                </ul>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click <strong>Save</strong>
                </span>
              </li>
            </ol>

            {/* Advanced COD Settings */}
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-systemTitle mb-2">
                Advanced COD Settings:
              </h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
                <li>
                  Restrict COD availability to specific{" "}
                  <strong>regions or shipping zones</strong>.
                </li>
              </ul>
            </div>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> Pre-set options for
                COD forms make setup quick and flexible, letting you control
                which information is collected without slowing down the checkout
                process.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar - Hidden on mobile, visible on desktop */}
      <div className="hidden lg:block lg:w-64 flex-shrink-0">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#online-payments"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "online-payments"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Online Payments
              </a>
            </li>
            <li>
              <a
                href="#cash-on-delivery"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "cash-on-delivery"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Cash on Delivery
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ConfigurePayment;
